/**
 * Strategies library (G17).
 *
 * Reads ``GET /weight-vectors`` (the only-latest view), groups by
 * strategy_template, and renders one card per template showing the seeded
 * default plus any custom forks. Operators can:
 *
 *   - Seed all default strategies via ``POST /scoring/seed-strategies``.
 *   - Clone any strategy into a new CUSTOM weight vector with editable
 *     name + weights (cloning bumps a new vector under a new name, the
 *     backend versioning handles same-name forks separately).
 *   - Activate or deactivate a vector for its name.
 *
 * Note: the user runs this single-operator, so we present every vector
 * the API returns. A future "favourites" cut would slot in as a chip row.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Library, RotateCcw } from "lucide-react";
import {
  activateWeightVector,
  createWeightVector,
  listWeightVectors,
  seedStrategies,
} from "@/lib/api";
import type { WeightVectorOut } from "@/lib/api/client";
import { Panel } from "@/components/ui/Panel";
import { Pill } from "@/components/ui/Pill";
import { Button } from "@/components/ui/Button";
import { Label, TextInput } from "@/components/ui/Input";
import { DataTable } from "@/components/ui/Table";
import { EmptyState, LoadingState } from "@/components/ui/States";

type TemplateGroup = {
  template: string;
  vectors: WeightVectorOut[];
};

export function StrategiesPage() {
  const qc = useQueryClient();
  const vectors = useQuery({
    queryKey: ["weight-vectors", "all"],
    queryFn: () => listWeightVectors(),
  });

  const seed = useMutation({
    mutationFn: seedStrategies,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["weight-vectors"] }),
  });

  const activate = useMutation({
    mutationFn: (id: number) => activateWeightVector(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["weight-vectors"] }),
  });

  const groups: TemplateGroup[] = useMemo(() => {
    const map = new Map<string, WeightVectorOut[]>();
    (vectors.data ?? []).forEach((v) => {
      const list = map.get(v.strategy_template) ?? [];
      list.push(v);
      map.set(v.strategy_template, list);
    });
    return Array.from(map.entries())
      .map(([template, vectors]) => ({
        template,
        vectors: [...vectors].sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.template.localeCompare(b.template));
  }, [vectors.data]);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="space-y-0.5">
          <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
            <Library size={18} />
            Strategy library
          </h1>
          <p className="text-2xs text-foreground-muted">
            Browse seeded strategies, fork into custom weight vectors, and
            activate the one your morning scorer should target.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => seed.mutate()}
            disabled={seed.isPending}
            leadingIcon={<RotateCcw size={12} />}
          >
            {seed.isPending ? "Seeding…" : "Seed defaults"}
          </Button>
        </div>
      </header>

      {seed.data && (
        <Panel>
          <div className="text-xs text-foreground-secondary">
            Seed result:{" "}
            {Object.entries(seed.data).map(([template, status]) => (
              <span key={template} className="mr-2">
                <span className="font-mono">{template}</span>={" "}
                <Pill tone={status === "created" ? "good" : "muted"}>
                  {status}
                </Pill>
              </span>
            ))}
          </div>
        </Panel>
      )}

      {vectors.isLoading && <LoadingState message="Loading strategies…" />}
      {vectors.data && vectors.data.length === 0 && (
        <EmptyState
          title="No strategies yet"
          description="Click 'Seed defaults' to insert the six starter weight vectors."
        />
      )}

      {groups.map((g) => (
        <StrategyGroupPanel
          key={g.template}
          group={g}
          onActivate={(id) => activate.mutate(id)}
          activating={activate.isPending}
        />
      ))}
    </div>
  );
}

function StrategyGroupPanel({
  group,
  onActivate,
  activating,
}: {
  group: TemplateGroup;
  onActivate: (id: number) => void;
  activating: boolean;
}) {
  const navigate = useNavigate();
  return (
    <Panel
      title={
        <span className="flex items-center gap-2 capitalize">
          {prettyTemplate(group.template)}
          <Pill tone="muted">{group.vectors.length}</Pill>
        </span>
      }
      description={describeTemplate(group.template)}
    >
      <DataTable
        dense
        rows={group.vectors}
        rowKey={(v) => v.id}
        columns={[
          {
            key: "name",
            header: "Name",
            cell: (v) => (
              <button
                type="button"
                onClick={() =>
                  navigate(`/settings?weight_vector_id=${v.id}`)
                }
                className="text-left font-medium text-foreground-primary hover:text-accent-strong"
              >
                {v.name}
              </button>
            ),
          },
          {
            key: "version",
            header: "v",
            align: "right",
            cell: (v) => v.version,
          },
          {
            key: "asset_class",
            header: "Asset class",
            cell: (v) =>
              v.applicable_security_type ? (
                <Pill tone="accent">{v.applicable_security_type}</Pill>
              ) : (
                <span className="text-foreground-muted">any</span>
              ),
          },
          {
            key: "factors",
            header: "Factors",
            align: "right",
            cell: (v) => Object.keys(v.weights).length,
          },
          {
            key: "is_active",
            header: "Active",
            cell: (v) =>
              v.is_active ? (
                <Pill tone="good">on</Pill>
              ) : (
                <button
                  type="button"
                  onClick={() => onActivate(v.id)}
                  disabled={activating}
                  className="text-2xs text-accent-strong hover:underline disabled:opacity-50"
                >
                  activate
                </button>
              ),
          },
          {
            key: "actions",
            header: "",
            cell: (v) => <CloneButton vector={v} />,
            align: "right",
          },
        ]}
      />
    </Panel>
  );
}

function CloneButton({ vector }: { vector: WeightVectorOut }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(`${vector.name} (copy)`);

  const clone = useMutation({
    mutationFn: () =>
      createWeightVector({
        name,
        description: vector.description
          ? `Fork of "${vector.name}". ${vector.description}`
          : `Fork of "${vector.name}".`,
        weights: { ...vector.weights },
        strategy_template: "custom",
        applicable_security_type: vector.applicable_security_type,
        activate: false,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["weight-vectors"] });
      setOpen(false);
    },
  });

  if (!open) {
    return (
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
        Clone
      </Button>
    );
  }
  return (
    <div className="flex items-end gap-2">
      <div>
        <Label>New name</Label>
        <TextInput
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-48"
        />
      </div>
      <Button
        variant="primary"
        size="sm"
        onClick={() => clone.mutate()}
        disabled={clone.isPending || name.trim().length === 0}
      >
        {clone.isPending ? "Cloning…" : "Save"}
      </Button>
      <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
        Cancel
      </Button>
    </div>
  );
}

function prettyTemplate(t: string): string {
  return t.replace(/_/g, " ");
}

function describeTemplate(t: string): string {
  switch (t) {
    case "quality_compounder":
      return "High-ROIC compounders with durable growth; quality at the centre, valuation discipline via FCF yield.";
    case "value":
      return "Multi-metric deep value screen with a quality guard against value traps.";
    case "garp":
      return "Growth at a reasonable price — balanced growth and valuation with a quality overlay.";
    case "momentum":
      return "Cross-sectional price momentum with analyst-revision breadth and a volatility penalty.";
    case "etf_core":
      return "Low-cost, liquid ETF selection with momentum and volatility overlays.";
    case "crypto_momentum":
      return "Multi-window momentum for digital assets; no fundamentals required.";
    case "custom":
      return "Operator-defined forks. Saved via the clone button on any seeded strategy.";
    default:
      return "";
  }
}
